import _jsx from "@babel/runtime/helpers/jsx";
var _FordUrgencyBadge, _FordUrgencyBadge2, _FordUrgencyBadge3, _Text, _Badge;
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import { useBrand } from '@atc/brand';
import { Badge, Glyphicon, LazyRender, Text } from 'reaxl';
import { PricingV2, ListingPaymentCTA, PricingBadges } from 'reaxl-molecules';
import { WalletPaymentDetailsV2 } from 'reaxl-wallet';
import InventoryListingInfo from '../InventoryListingInfo/index.js';
import InventoryListingTitle from '../InventoryListingTitle/index.js';
import InventoryListingBadges from '../InventoryListingBadges/index.js';
const FordUrgencyBadge = /*#__PURE__*/React.memo(_ref => {
  let {
    label
  } = _ref;
  const color = label === 'Reduced Price' ? 'success' : 'accent';
  const dataCmp = label.replace(/\s+/g, '').charAt(0).toLowerCase() + label.slice(1);
  return /*#__PURE__*/_jsx(Text, {
    "data-cmp": dataCmp,
    weight: "bold",
    color: color,
    antialiased: true
  }, void 0, label);
});
const UrgencyBadges = /*#__PURE__*/React.memo(_ref2 => {
  let {
    isNewlyListed,
    isPreorder,
    isReducedPrice,
    isHot,
    uiContext,
    isFord
  } = _ref2;
  let urgencyBadge = null;
  if (isHot && uiContext !== 'snapshot') {
    urgencyBadge = _FordUrgencyBadge || (_FordUrgencyBadge = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "High Demand"
    }));
  } else if (isNewlyListed) {
    urgencyBadge = _FordUrgencyBadge2 || (_FordUrgencyBadge2 = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "Newly Listed"
    }));
  } else if (isReducedPrice) {
    urgencyBadge = _FordUrgencyBadge3 || (_FordUrgencyBadge3 = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "Reduced Price"
    }));
  }
  if (!isFord && !isPreorder) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    className: "padding-bottom-1 margin-right-2"
  }, "badges", isFord && urgencyBadge, isPreorder && (_Text || (_Text = /*#__PURE__*/_jsx(Text, {
    className: "margin-right-2",
    "data-cmp": "customBuild",
    antialiased: true,
    color: "accent",
    weight: "bold"
  }, void 0, "Custom Order"))));
});
const PricingSection = /*#__PURE__*/React.memo(_ref3 => {
  let {
    isPreorder,
    priceLabel,
    priceDescription,
    onPricingDetailClick,
    orientation,
    pricingDetail,
    hasIncentive,
    showPayments,
    listing,
    renderPaymentDetails,
    showListingPaymentRibbon,
    enableMyWallet,
    onPaymentCTAClick,
    uiContext
  } = _ref3;
  const pricingContext = showPayments ? 'listing' : 'details';
  const pricingUiContext = uiContext === 'recommended' ? 'default' : pricingContext;
  const pricingComponent = /*#__PURE__*/_jsx(PricingV2, {
    data: pricingDetail,
    isPreorder: isPreorder,
    priceLabel: priceLabel,
    priceDescription: priceDescription,
    uiContext: pricingUiContext,
    onPricingDetailClick: onPricingDetailClick,
    orientation: orientation
  }, "inventory-listing-pricing");
  const isRecommended = uiContext === 'recommended';
  const isSnapshot = uiContext === 'snapshot';
  const paymentCTAComponent = /*#__PURE__*/_jsx(ListingPaymentCTA, {
    className: "text-size-200",
    ctaText: isRecommended ? 'See details' : 'See payment',
    onPaymentCTAClick: onPaymentCTAClick
  }, "paymentCTA");
  const showCTA = !isSnapshot && enableMyWallet && !listing.accelerate;
  return /*#__PURE__*/_jsx("div", {}, "pricing-section", /*#__PURE__*/_jsx("div", {
    className: "pricing-payments"
  }, void 0, !isRecommended && pricingComponent, !isSnapshot && showListingPaymentRibbon && renderPaymentDetails(), !isRecommended && !showListingPaymentRibbon && showCTA && paymentCTAComponent, isRecommended && pricingComponent, isRecommended && showCTA && paymentCTAComponent), /*#__PURE__*/_jsx(PricingBadges, {
    dealIndicator: pricingDetail.dealIndicator,
    hasIncentive: hasIncentive,
    uiContext: typeof uiContext === 'string' ? uiContext : undefined
  }));
});
/**
 *  Describe the component
 *
 */
function InventoryListingBodyV2(_ref4) {
  let {
    enableMyWallet = false,
    isActiveDeal,
    isSaved,
    listing,
    listingCTA,
    priceLabel,
    priceDescription,
    onCertifiedTileClick,
    onContextMenu,
    onMyWalletClick,
    onPaymentCTAClick,
    onPricingDetailClick,
    onProductTileClick,
    onProductTileLinkClick,
    onVhrBadgeClick,
    onSaveToggle,
    orientation,
    paymentsCTA,
    panelHeaderText,
    personalPaymentProps = {},
    showListingPaymentRibbon = false,
    showPackages = true,
    showPaymentDetailsLink,
    uiContext,
    updateListingPayments,
    ...rest
  } = _ref4;
  const {
    enable_cpo_vhr_badges: [, {
      show_cpo_vhr_badges: enableCpoVhrBadges
    }],
    enable_vhr_badges: [enableVhrBadges]
  } = useFeatures(['enable_cpo_vhr_badges', 'enable_vhr_badges']);
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  const {
    isNewlyListed,
    isReducedPrice,
    isLessMileage,
    isHot,
    pricingDetail = {},
    hasIncentive,
    title,
    listingType,
    certifiedTiles = [],
    year,
    make,
    model,
    trim,
    fuelType,
    specifications
  } = listing;
  const isSnapshot = uiContext === 'snapshot';
  const isPreorder = uiContext === 'preorder';
  const isSimplifiedListing = uiContext === 'simplified';
  const renderPaymentDetails = className => !isActiveDeal && /*#__PURE__*/_jsx(LazyRender, {
    height: 22
  }, "paymentDetails", /*#__PURE__*/_jsx("div", {
    className: clsx(className, 'display-flex')
  }, void 0, /*#__PURE__*/_jsx(WalletPaymentDetailsV2, {
    listingId: listing.id,
    isPreorder: isPreorder,
    onMyWalletClick: onMyWalletClick,
    personalPaymentProps: personalPaymentProps,
    uiContext: uiContext === 'recommended' ? 'default' : 'ribbon',
    updateListingPayments: updateListingPayments
  })));
  const titleProps = {
    ...listingCTA,
    title: title || listingCTA?.title,
    size: 400,
    onContextMenu,
    uiContext
  };
  const listingInfoProps = {
    year,
    make: make?.name,
    model: model?.name,
    trim: trim?.name,
    mileage: specifications?.mileage?.value,
    engine: specifications?.engine?.value,
    fuelType,
    fuelGroup: fuelType?.group,
    listingType,
    certifiedTiles,
    onCertifiedTileClick
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: "inventory-listing-body padding-top-3 padding-horizontal-3"
  }, rest), uiContext === 'recommended' && /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-12 margin-bottom-1"
  }, void 0, /*#__PURE__*/_jsx(Text, {
    size: 400,
    color: "blue-darker",
    weight: "bold"
  }, void 0, panelHeaderText))), /*#__PURE__*/_jsx("div", {}, void 0, (isNewlyListed || isPreorder || isReducedPrice || isHot) && /*#__PURE__*/_jsx(UrgencyBadges, {
    isNewlyListed: isNewlyListed,
    isPreorder: isPreorder,
    isReducedPrice: isReducedPrice,
    isHot: isHot,
    uiContext: uiContext,
    isFord: isFord
  }), /*#__PURE__*/_jsx("div", {
    className: "display-flex justify-content-between"
  }, "title", isPreorder ? /*#__PURE__*/React.createElement(InventoryListingTitle, titleProps) : /*#__PURE__*/React.createElement(InventoryListingInfo, Object.assign({}, titleProps, listingInfoProps))), /*#__PURE__*/_jsx(PricingSection, {
    isPreorder: isPreorder,
    listing: listing,
    priceLabel: priceLabel,
    priceDescription: priceDescription,
    pricingDetail: pricingDetail,
    hasIncentive: hasIncentive,
    onPricingDetailClick: onPricingDetailClick,
    orientation: orientation,
    showPayments: !isSnapshot,
    renderPaymentDetails: renderPaymentDetails,
    showListingPaymentRibbon: showListingPaymentRibbon,
    enableMyWallet: enableMyWallet,
    onPaymentCTAClick: onPaymentCTAClick,
    uiContext: uiContext
  }), !isSimplifiedListing && !isSnapshot && /*#__PURE__*/_jsx("div", {
    className: "margin-top-4"
  }, "listing-badges", /*#__PURE__*/_jsx(InventoryListingBadges, {
    enableCertifiedBadges: isPreorder,
    enableCpoVhrBadges: enableCpoVhrBadges,
    enableVhrBadges: enableVhrBadges,
    enableFuelTypeBadges: isPreorder,
    listing: listing,
    onCertifiedTileClick: onCertifiedTileClick,
    onProductTileClick: onProductTileClick,
    onVhrBadgeClick: onVhrBadgeClick
  }))), isLessMileage && uiContext === 'snapshot' && (_Badge || (_Badge = /*#__PURE__*/_jsx(Badge, {
    className: "display-inline-flex gap-1 align-items-baseline text-normal text-size-200 margin-top-3 warning"
  }, void 0, /*#__PURE__*/_jsx(Glyphicon, {
    glyph: "mileage",
    className: "warning text-size-300"
  }), "Less Mileage"))));
}
export default InventoryListingBodyV2;